import "@material/web/button/filled-button";
import "@material/web/button/outlined-button";

import { Overlay } from "@angular/cdk/overlay";
import { CommonModule, NgClass, NgStyle } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { Observable } from "rxjs";

import { RouterModule } from "@angular/router";
import { NgImageSliderModule } from "ng-image-slider";
import { NextimeWrapperComponent } from "../nextime-wrapper/nextime-wrapper.component";
import { DateFormatPipe } from "../pipes/date-format-pipe";
import { ProcessNowDialogComponent } from "../process-now-dialog/process-now-dialog.component";
import { ResumeDialogComponent } from "../resume-dialog/resume-dialog.component";
import { ScheduledOrderPaymentMethodComponent } from "../scheduled-order-payment-method/scheduled-order-payment-method.component";
import { QPilotProduct } from "../types/product";
import {
  getAddressDisplayName,
  getChangeLimitDate,
  getFailedReason,
  getStatusDisplayName,
  OrderStatus,
  QPilotScheduledOrder,
  QPilotScheduledOrderTotals,
} from "../types/scheduled-order";
import { QPilotSiteSettings } from "./../types/site";
import { RescheduleOrderEvent } from "../types/events";
import { NextimeService } from "../../services/nextime.service";
import { getCleanProductTitle } from "../../utils";

const STATUS_ICON_MAP = {
  active: "cycle",
  failed: "error",
  paused: "autopause",
  processing: "pending",
  queued: "pending",
  deleted: "cycle",
};

export interface ImageObject {
  image: string;
  thumbImage: string;
  title?: string;
  price?: string;
}

@Component({
  selector: "lib-scheduled-order-card",
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    HttpClientModule,
    NgStyle,
    NgClass,
    DateFormatPipe,
    ProcessNowDialogComponent,
    ResumeDialogComponent,
    NextimeWrapperComponent,
    ScheduledOrderPaymentMethodComponent,
    RouterModule,
    NgImageSliderModule,
  ],
  providers: [DateFormatPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./scheduled-order-card.component.html",
  styleUrl: "./scheduled-order-card.component.scss",
})
export class ScheduledOrderCardComponent implements OnChanges, OnInit {
  @Input() order: QPilotScheduledOrder & {
    getOrderTotals: () => Observable<QPilotScheduledOrderTotals>;
    closestNextOccurrenceDate?: string;
  };
  @Input() orderVerboseName: string = "Scheduled Order";
  @Input() orderVerbosePluralName: string = "Scheduled Orders";
  @Input() collapsed = true;
  @Input() useNextime: boolean;
  @Input() nextimeToken: string;
  @Input() nextimeSiteId: number;
  @Input() attachNextimeDialogTo: string | undefined;
  @Input() siteSettings: QPilotSiteSettings;
  @Input() closestDeliveryDateUtc: string | undefined;
  @Input() scheduledOrder: QPilotScheduledOrder;

  @Output() processOrder = new EventEmitter();
  @Output() detail = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() pause = new EventEmitter();
  @Output() cancel = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() resume = new EventEmitter();
  @Output() reschedule = new EventEmitter();
  @Output() rescheduleAndResume = new EventEmitter();
  @Output() updateNOD = new EventEmitter<{ dateValid: boolean; date: Date }>();
  @Output() clickResume = new EventEmitter();
  @Output() updatePayment = new EventEmitter();
  @Output() deliveryDateUpdate = new EventEmitter();
  @Output() updateNextOccurrence = new EventEmitter<RescheduleOrderEvent>();
  @Output() processNowDeliveryDateFetched = new EventEmitter();

  @ViewChild(NextimeWrapperComponent) nextime: NextimeWrapperComponent;

  statusIcon = "";
  thumbnailStyle: { [property: string]: string };
  itemsCount: number;
  orderStatus: OrderStatus;
  changeLimitDate: Date;
  addressDisplayName: string;
  statusDisplayName: string;
  cardCollapsedDisplayName: string;
  cardExpandedDisplayName: string;
  orderTotals$: Observable<QPilotScheduledOrderTotals>;
  orderDescription: string;
  dialogRef: MatDialogRef<unknown>;

  imageObject: ImageObject[] = [];

  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
    private dateFormatPipe: DateFormatPipe,
    private zone: NgZone,
    private nextimeService: NextimeService,
  ) {}

  ngOnInit() {
    this.itemsCount =
      this.order.scheduledOrderItems?.reduce(
        (a, b) => a + (b.quantity ?? 0),
        0,
      ) ?? 0;

    this.imageObject = this.getProductImages();

    // Apply image dimension settings from site settings
    this.applyImageDimensionSettings();

    // Initialize orderTotals$ once to prevent duplicate API calls
    this.orderTotals$ = this.order.getOrderTotals();
  }

  /**
   * Applies the image dimension settings from site settings to CSS variables
   * Uses default values if settings are null: height 700px, width auto
   */
  private applyImageDimensionSettings() {
    const root = document.documentElement;
    const maxWidth = this.siteSettings?.styles?.maxImageWidthPx;
    const maxHeight = this.siteSettings?.styles?.maxImageHeightPx;

    // Set max width with default of 'auto' if null
    if (maxWidth) {
      root.style.setProperty("--max-image-width", `${maxWidth}px`);
    } else {
      root.style.setProperty("--max-image-width", "auto");
    }

    // Set max height with default of 700px if null
    if (maxHeight) {
      root.style.setProperty("--max-image-height", `${maxHeight}px`);
    } else {
      root.style.setProperty("--max-image-height", "700px");
    }
  }

  ngOnChanges() {
    this.orderStatus = this.order.status;
    this.statusIcon = STATUS_ICON_MAP[this.orderStatus];
    this.thumbnailStyle = { backgroundImage: `url(${this.getThumbnail()})` };

    this.changeLimitDate = getChangeLimitDate(this.order);
    this.addressDisplayName = getAddressDisplayName(this.order);

    this.statusDisplayName =
      this.orderStatus === "failed"
        ? `${getStatusDisplayName(
            this.order,
            this.siteSettings.localization,
          )} (${getFailedReason(this.order.processingErrorCode)})`
        : getStatusDisplayName(this.order, this.siteSettings.localization);

    this.cardCollapsedDisplayName = this.getCardCollapsedDisplayName();
    this.cardExpandedDisplayName = this.getCardExpandedDisplayName();

    // Re-apply image dimension settings when site settings change
    this.applyImageDimensionSettings();

    // Reinitialize orderTotals$ when order changes
    if (this.order) {
      this.orderTotals$ = this.order.getOrderTotals();
    }
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  }

  getChangeLimitDate(order: QPilotScheduledOrder) {
    return getChangeLimitDate(order);
  }

  onResume() {
    if (!this.isResumable) return;
    this.resume.emit({
      orderId: this.order.id,
    });
  }

  onPause() {
    if (!this.isActive) return;
    this.pause.emit({
      orderId: this.order.id,
    });
  }

  onCancel() {
    this.cancel.emit({
      orderId: this.order.id,
    });
  }

  onDetail() {
    this.detail.emit({
      orderId: this.order.id,
    });
  }

  onReschedule() {
    this.reschedule.emit({
      orderId: this.order.id,
    });
  }

  onRescheduleAndResume() {
    this.rescheduleAndResume.emit({
      orderId: this.order.id,
    });
  }

  onUpdateNOD(event: { dateValid: boolean; date: Date }) {
    this.updateNOD.emit({
      dateValid: event.dateValid,
      date: event.date,
    });
  }

  isResumeWithDatePickerNeeded() {
    // Check if NextOccurrenceUtc is in the past
    const nextOccurrence = new Date(this.order.nextOccurrenceUtc);
    const today = new Date();
    return nextOccurrence < today;
  }

  onUpdatePayment() {
    this.updatePayment.emit({
      orderId: this.order.id,
    });
  }

  OnupdateNOD(ev: RescheduleOrderEvent) {
    this.updateNOD.emit({
      dateValid: true,
      date: ev.nextOccurrenceUtc,
    });
  }

  onProcessOrder() {
    this.processOrder.emit({
      orderId: this.order.id,
    });
    this.dialogRef.close();
  }

  onDeliveryDateUpdate(event: Event) {
    this.deliveryDateUpdate.emit(event);
  }

  onProcessNowDeliveryDateFetched(event: Event) {
    this.processNowDeliveryDateFetched.emit(event);
  }

  async openDeliveryDateDialog() {
    await this.nextime.openDeliveryDateDialog();
  }

  openDialogWithRef(templateRef: TemplateRef<unknown>) {
    this.zone.run(() => {
      this.dialogRef = this.dialog.open(templateRef, {
        scrollStrategy: this.overlay.scrollStrategies.noop(),
      });
    });
  }

  private getCardCollapsedDisplayName() {
    if (this.effectiveUseNextime && this.hasDeliveryDate && this.isActive) {
      const nextDeliveryDate = `Next Delivery: ${this.dateFormatPipe.transform(
        this.order.estimatedDeliveryDate,
        "MMM d",
      )}`;
      if (this.siteSettings.isDisplayScheduledOrderId) {
        return `#${this.order.id} - ${nextDeliveryDate}`;
      }
      return nextDeliveryDate;
    }

    if (this.isProcessing)
      return getStatusDisplayName(this.order, this.siteSettings.localization);
    if (this.isPaused)
      return `${getStatusDisplayName(
        this.order,
        this.siteSettings.localization,
      )}: Resume Now`;
    if (this.isFailed)
      return `${getStatusDisplayName(
        this.order,
        this.siteSettings.localization,
      )}: ${getFailedReason(this.order.processingErrorCode)}`;

    return this.getFormattedNextOrderDate(this.order);
  }

  private getCardExpandedDisplayName() {
    if (this.effectiveUseNextime && this.hasDeliveryDate && this.isActive) {
      const nextDeliveryDate = `Next Delivery: ${this.dateFormatPipe.transform(
        this.order.estimatedDeliveryDate,
        "MMM d",
      )}`;
      if (this.siteSettings.isDisplayScheduledOrderId) {
        return `#${this.order.id} - ${nextDeliveryDate}`;
      }
      return nextDeliveryDate;
    }

    if (this.isProcessing) return `${this.orderVerboseName} is processing`;

    return this.getFormattedNextOrderDate(this.order);
  }

  private get isActive() {
    return this.orderStatus == "active";
  }

  private get isPaused() {
    return this.orderStatus == "paused";
  }

  private get isDeleted() {
    return this.orderStatus == "deleted";
  }

  private get isProcessing() {
    return this.orderStatus == "processing";
  }

  private get isFailed() {
    return this.orderStatus == "failed";
  }

  private get isResumable() {
    return (
      this.isPaused ||
      this.isDeleted ||
      this.order?.processingErrorCode === 2000
    );
  }

  get hasDeliveryDate() {
    return (
      !!this.order.estimatedDeliveryDate?.trim() && this.effectiveUseNextime
    );
  }

  get effectiveUseNextime(): boolean {
    return (
      this.useNextime &&
      this.nextimeService.getShouldDisplayDeliveryDate() &&
      this.nextimeService.getShouldAlignNextOrderDate()
    );
  }

  private getProductImageUrl(product: QPilotProduct | undefined) {
    return (
      product?.imageUrl ??
      product?.metadata?.imageUrl ??
      product?.metadata?.imageThumbUrl ??
      ""
    );
  }

  private getProductImages(): ImageObject[] {
    return (
      this.order.scheduledOrderItems?.map((item) => {
        const title = this.getCleanProductTitle(item.product?.title);
        return {
          image: this.getProductImageUrl(item.product),
          thumbImage: this.getProductImageUrl(item.product),
          title: !this.siteSettings?.showProductTitleOnImages
            ? `${title}`
            : undefined,
        };
      }) ?? []
    );
  }

  protected getThumbnail() {
    const item = this.order.scheduledOrderItems?.find((item) => {
      const imageUrl = this.getProductImageUrl(item.product);
      return imageUrl.trim() !== "";
    });
    return this.getProductImageUrl(item?.product) ?? "";
  }

  private getFormattedNextOrderDate({
    nextOccurrenceUtc,
  }: QPilotScheduledOrder) {
    const nextOrderDate = `Next Order Date: ${this.dateFormatPipe.transform(
      nextOccurrenceUtc,
      "MMM d",
    )}`;
    if (this.siteSettings.isDisplayScheduledOrderId) {
      return `#${this.order.id} - ${nextOrderDate}`;
    }
    return nextOrderDate;
  }

  getCleanProductTitle = getCleanProductTitle;
}
